import React, { useState } from "react";
import { api, money, fileSrc } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { toast } from "sonner";
import { ScanLine, Loader2, X, Plus, Trash2, Split, ImagePlus } from "lucide-react";
import { SupplierPicker } from "./SupplierPicker";

const inputCls = "w-full rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none transition";
const DONE = ["completed", "completed_no_charge", "cancelled"];

export const SplitReceipt = ({ onDone }) => {
  const { t } = useLang();
  const [stage, setStage] = useState("idle"); // idle | scanning | review | saving
  const [tickets, setTickets] = useState([]);
  const [supplier, setSupplier] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [items, setItems] = useState([]);
  const [tax, setTax] = useState(0);
  const [photoUrl, setPhotoUrl] = useState("");

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setStage("scanning");
    try {
      const [up, tks] = await Promise.all([api.upload(file), api.get("/tickets")]);
      setTickets(tks.filter((tk) => !DONE.includes(tk.status)));
      setPhotoUrl(up.url || "");
      const res = await api.post("/purchasing/scan-receipt", { file_ids: [up.id] });
      setSupplier(res.supplier || "");
      if (res.date) setDate(res.date);
      setTax(res.tax || 0);
      setItems((res.items || []).map((i) => ({ ...i, ticket_id: "" })));
      setStage("review");
      if (!res.items?.length) toast.message(t("noItemsFound"));
    } catch { toast.error(t("error")); setStage("idle"); }
    finally { e.target.value = ""; }
  };

  const upd = (i, k, v) => setItems(items.map((it, j) => j === i ? { ...it, [k]: (k === "description" || k === "ticket_id") ? v : parseFloat(v) || 0 } : it));
  const addRow = () => setItems([...items, { description: "", quantity: 1, unit_price: 0, ticket_id: "" }]);
  const del = (i) => setItems(items.filter((_, j) => j !== i));
  const applyAll = (tid) => setItems(items.map((it) => ({ ...it, ticket_id: tid })));

  const subtotal = items.reduce((s, it) => s + (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0), 0);
  const grand = subtotal + (parseFloat(tax) || 0);

  const save = async () => {
    const clean = items.filter((i) => i.description.trim());
    if (clean.length === 0) return toast.error(t("noItemsFound"));
    if (clean.some((i) => !i.ticket_id)) return toast.error(t("assignAllItems"));
    const byTicket = {};
    clean.forEach((i) => {
      byTicket[i.ticket_id] = byTicket[i.ticket_id] || [];
      byTicket[i.ticket_id].push({ ...i, quantity: i.quantity, unit_price: i.unit_price });
    });
    setStage("saving");
    try {
      const res = await api.post("/purchasing/split-receipt", {
        supplier, purchased_date: new Date(date).toISOString(), tax: parseFloat(tax) || 0,
        photos: photoUrl ? [photoUrl] : [],
        allocations: Object.entries(byTicket).map(([ticket_id, its]) => ({ ticket_id, items: its })),
      });
      toast.success(`${t("receiptSplitDone")}: ${res.allocations.map((a) => `${a.ticket_number} ${money(a.total)}`).join(" · ")}`);
      setStage("idle"); setItems([]); setSupplier(""); setTax(0); setPhotoUrl("");
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error"));
      setStage("review");
    }
  };

  return (
    <>
      {stage === "scanning" ? (
        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-violet-700"><Loader2 className="w-4 h-4 animate-spin" />{t("scanning")}</span>
      ) : (
        <label data-testid="split-receipt-btn" className="inline-flex items-center gap-2 rounded-lg bg-violet-600 text-white text-sm font-medium px-4 py-2.5 hover:bg-violet-700 cursor-pointer transition-colors">
          <Split className="w-4 h-4" /> {t("splitReceipt")}
          <input type="file" accept="image/*,application/pdf" className="hidden" onChange={onFile} />
        </label>
      )}

      {(stage === "review" || stage === "saving") && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={() => stage === "review" && setStage("idle")}>
          <div data-testid="split-receipt-modal" className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 sticky top-0 bg-white z-10">
              <h2 className="text-base font-semibold text-slate-900 inline-flex items-center gap-2"><Split className="w-4 h-4 text-violet-600" />{t("splitReceipt")}</h2>
              <button onClick={() => setStage("idle")} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-xs text-slate-500">{t("splitReceiptHint")}</p>
              <div className="flex items-center gap-3">
                {photoUrl && (
                  <a href={fileSrc(photoUrl)} target="_blank" rel="noreferrer" className="shrink-0">
                    <img src={fileSrc(photoUrl)} alt="" className="w-16 h-16 rounded-md object-cover border border-slate-200" />
                  </a>
                )}
                <div className="grid grid-cols-2 gap-3 flex-1">
                  <div><label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">{t("supplier")}</label><SupplierPicker value={supplier} onChange={setSupplier} testId="sr-supplier" /></div>
                  <div><label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">{t("purchasedOn")}</label><input data-testid="sr-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} /></div>
                </div>
              </div>

              <div className="flex items-center gap-2 text-xs">
                <span className="text-slate-500">{t("applyToAll")}:</span>
                <select data-testid="sr-apply-all" onChange={(e) => e.target.value && applyAll(e.target.value)} defaultValue="" className={`${inputCls} max-w-[280px] py-1`}>
                  <option value="">—</option>
                  {tickets.map((tk) => <option key={tk.id} value={tk.id}>{tk.number} — {tk.title}</option>)}
                </select>
              </div>

              <div className="space-y-2">
                <div className="grid grid-cols-12 gap-2 text-[10px] font-bold uppercase text-slate-400 px-0.5">
                  <span className="col-span-4">{t("items")}</span><span className="col-span-1">{t("qty")}</span>
                  <span className="col-span-2">{t("unitPrice")}</span><span className="col-span-4">{t("serviceCall")}</span><span />
                </div>
                {items.map((it, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <input data-testid={`sr-desc-${i}`} value={it.description} onChange={(e) => upd(i, "description", e.target.value)} placeholder="Item" className={`${inputCls} col-span-4`} />
                    <input type="number" step="0.01" value={it.quantity} onChange={(e) => upd(i, "quantity", e.target.value)} className={`${inputCls} col-span-1`} />
                    <input type="number" step="0.01" value={it.unit_price} onChange={(e) => upd(i, "unit_price", e.target.value)} className={`${inputCls} col-span-2`} />
                    <select data-testid={`sr-ticket-${i}`} value={it.ticket_id} onChange={(e) => upd(i, "ticket_id", e.target.value)}
                      className={`${inputCls} col-span-4 ${!it.ticket_id ? "border-amber-400 bg-amber-50" : ""}`}>
                      <option value="">{t("selectServiceCall")}</option>
                      {tickets.map((tk) => <option key={tk.id} value={tk.id}>{tk.number} — {tk.title}</option>)}
                    </select>
                    <button onClick={() => del(i)} className="col-span-1 flex justify-center text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
                <button onClick={addRow} className="text-xs font-medium text-violet-700 inline-flex items-center gap-1"><Plus className="w-3.5 h-3.5" />{t("addItem")}</button>
              </div>

              <div className="border-t border-slate-200 pt-3 space-y-1.5">
                <div className="flex items-center justify-between text-sm"><span className="text-slate-500">{t("subtotal")}</span><span className="font-medium text-slate-700 tabular-nums">{money(subtotal)}</span></div>
                <div className="flex items-center justify-between text-sm"><span className="text-slate-500">{t("tax")} <span className="text-[10px] text-slate-400">({t("taxSplitHint")})</span></span><input data-testid="sr-tax" type="number" step="0.01" value={tax} onChange={(e) => setTax(e.target.value)} className={`${inputCls} w-28 text-right`} /></div>
                <div className="flex items-center justify-between text-base font-bold text-slate-900 border-t border-slate-200 pt-1.5"><span>{t("total")}</span><span className="tabular-nums">{money(grand)}</span></div>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-5 py-4 border-t border-slate-200 sticky bottom-0 bg-white">
              <button onClick={() => setStage("idle")} disabled={stage === "saving"} className="rounded-lg px-4 py-2 text-sm font-medium border border-slate-300 hover:bg-slate-50">{t("cancel")}</button>
              <button data-testid="sr-save" onClick={save} disabled={stage === "saving"} className="rounded-lg px-4 py-2 text-sm font-medium bg-slate-900 text-white hover:bg-slate-800 inline-flex items-center gap-2 disabled:opacity-60">
                {stage === "saving" && <Loader2 className="w-4 h-4 animate-spin" />}{t("save")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
